import { useId } from "react";
import { resolveChatWidget } from "@/lib/chatWidget";

/**
 * BuiltBuddy's animated avatar — a stylised, icon-like mascot (no human face: a friendly robot bust) drawn in
 * the BuiltOS palette, 100 % SVG + CSS keyframes (see "BuiltBuddy avatar" in index.css). Three styles:
 *   engineer  — steel-blue hard hat + a roll of drawings on the shoulder (tube tilts)
 *   builder   — amber site hat + tool belt + a hammer on the swing
 *   assistant — headset with a bobbing mic boom + a live "typing" chat badge
 * Every style blinks and bobs gently. `animated={false}` freezes it (thumbnails, reduced-motion is honoured in CSS).
 * Fills the box it is given (the launcher passes h-full w-full); clips itself to a circle.
 */

const Eyes = () => (
  <g className="bb-av-eyes" data-testid="builtbuddy-avatar-eyes">
    <circle cx="27" cy="30" r="2.4" className="fill-brand-navy" />
    <circle cx="37" cy="30" r="2.4" className="fill-brand-navy" />
  </g>
);

const Smile = () => <path d="M28 35.5 Q32 38.6 36 35.5" className="stroke-brand-navy" strokeWidth="1.8" strokeLinecap="round" fill="none" />;

/** Rounded robot head with a soft visor line — shared by every style. */
const Head = ({ children }) => (
  <g className="bb-av-head">
    <rect x="28" y="38" width="8" height="8" className="fill-brand-pale-soft" />
    <rect x="19" y="17" width="26" height="24" rx="9" className="fill-brand-pale" />
    <rect x="22" y="20" width="20" height="2" rx="1" className="fill-brand-pale-soft" />
    <Eyes />
    <Smile />
    {children}
  </g>
);

const HardHat = ({ tone }) => (
  <g className="bb-av-hat">
    <path d="M19 21 C19 11.5 25 7.5 32 7.5 C39 7.5 45 11.5 45 21 Z" className={tone === "amber" ? "fill-[hsl(var(--warning))]" : "fill-brand-steel"} />
    <rect x="29.5" y="6.5" width="5" height="7" rx="1.5" className={tone === "amber" ? "fill-[hsl(var(--warning))]" : "fill-brand-steel"} />
    <rect x="15" y="20" width="34" height="3.6" rx="1.8" className={tone === "amber" ? "fill-[hsl(var(--warning)/0.75)]" : "fill-brand-steel-deep"} />
    <rect x="25" y="11" width="3" height="7" rx="1.5" className="fill-white/40" />
  </g>
);

const Shoulders = () => <path d="M10 66 C10 51 19.5 46.5 32 46.5 C44.5 46.5 54 51 54 66 Z" className="fill-brand-steel" />;

const Engineer = () => (
  <>
    <Shoulders />
    {/* collar */}
    <path d="M27 46.5 L32 52 L37 46.5" className="fill-brand-pale" />
    {/* roll of drawings on the shoulder */}
    <g transform="rotate(-32 46 52)">
      <g className="bb-av-tube" data-testid="builtbuddy-avatar-blueprint">
        <rect x="35" y="48.5" width="22" height="7" rx="3.5" className="fill-brand-pale" />
        <rect x="39" y="50.5" width="14" height="1.2" rx="0.6" className="fill-brand-steel-deep" />
        <rect x="39" y="53" width="9" height="1.2" rx="0.6" className="fill-brand-steel-deep" />
        <ellipse cx="36.5" cy="52" rx="2" ry="3.5" className="fill-brand-pale-soft" />
        <ellipse cx="55.5" cy="52" rx="2" ry="3.5" className="fill-brand-pale-soft" />
      </g>
    </g>
    <Head>
      <HardHat tone="steel" />
    </Head>
  </>
);

const Builder = () => (
  <>
    <Shoulders />
    {/* tool belt */}
    <rect x="8" y="57" width="48" height="5" className="fill-brand-navy" />
    <rect x="29" y="56.5" width="6" height="6" rx="1" className="fill-brand-pale-soft" />
    <rect x="16" y="58" width="7" height="8" rx="1.5" className="fill-[hsl(var(--warning))]" />
    <rect x="41" y="58" width="7" height="8" rx="1.5" className="fill-[hsl(var(--warning))]" />
    {/* hammer on the swing — pivots at the grip */}
    <g className="bb-av-hammer" data-testid="builtbuddy-avatar-hammer">
      <rect x="50.5" y="34" width="3.4" height="22" rx="1.6" className="fill-brand-pale-soft" />
      <rect x="45" y="31" width="14.5" height="6.5" rx="2" className="fill-brand-navy" />
      <rect x="45" y="31" width="4" height="6.5" rx="1.5" className="fill-brand-slate" />
    </g>
    <Head>
      <HardHat tone="amber" />
    </Head>
  </>
);

const Assistant = () => (
  <>
    <Shoulders />
    <path d="M26 46.5 L32 53 L38 46.5" className="fill-brand-pale" />
    {/* live chat badge — three typing dots */}
    <g data-testid="builtbuddy-avatar-chat-badge">
      <rect x="23" y="53" width="18" height="9" rx="4.5" className="fill-brand-pale" />
      <circle cx="28" cy="57.5" r="1.5" className="bb-av-dot fill-brand-navy" />
      <circle cx="32" cy="57.5" r="1.5" className="bb-av-dot fill-brand-navy" />
      <circle cx="36" cy="57.5" r="1.5" className="bb-av-dot fill-brand-navy" />
    </g>
    <Head>
      {/* headset: band, ear cups, bobbing mic boom */}
      <path d="M19.5 29 C19.5 18 25 13 32 13 C39 13 44.5 18 44.5 29" className="stroke-brand-steel-deep" strokeWidth="2.6" fill="none" strokeLinecap="round" />
      <rect x="16.5" y="26" width="5" height="9" rx="2" className="fill-brand-steel-deep" />
      <rect x="42.5" y="26" width="5" height="9" rx="2" className="fill-brand-steel-deep" />
      <g className="bb-av-mic" data-testid="builtbuddy-avatar-mic">
        <path d="M45 34 C45 39.5 42 41.5 38.5 41.5" className="stroke-brand-steel-deep" strokeWidth="1.8" fill="none" strokeLinecap="round" />
        <circle cx="38" cy="41.5" r="2.1" className="fill-brand-steel" />
      </g>
    </Head>
  </>
);

const BODIES = { engineer: Engineer, builder: Builder, assistant: Assistant };

/**
 * @param {object} props
 * @param {"engineer"|"builder"|"assistant"} [props.style]  avatar style (defaults to the org setting's default, engineer)
 * @param {boolean} [props.animated]  false freezes every keyframe (thumbnails / pickers)
 * @param {string} [props.className]  sizing (e.g. "h-14 w-14"); the SVG clips itself to a circle
 */
export const BuiltBuddyAvatar = ({ style = "engineer", animated = true, className = "", title = "", ...rest }) => {
  const resolved = resolveChatWidget({ avatar_style: style }).avatar_style;
  const Body = BODIES[resolved];
  const clipId = `bb-av-clip-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  return (
    <svg
      viewBox="0 0 64 64"
      className={`bb-avatar ${animated ? "" : "bb-av-static"} ${className}`}
      role={title ? "img" : "presentation"}
      aria-label={title || undefined}
      aria-hidden={title ? undefined : true}
      focusable="false"
      data-testid="builtbuddy-avatar"
      data-avatar-style={resolved}
      data-animated={animated ? "true" : "false"}
      {...rest}
    >
      <defs>
        <clipPath id={clipId}>
          <circle cx="32" cy="32" r="32" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        <Body />
      </g>
    </svg>
  );
};
